import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { landingAsset } from "../utils/landingAsset";
import "./LandingPages.scss";

const SLIDES = [
  { img: "warehouse-planner.webp", title: "Plan your shipment" },
  { img: "five-carriers.webp", title: "One request, many carriers" },
  { img: "dock-signature.webp", title: "Efficient loading & unloading" },
];

const Home = () => {
  const navigate = useNavigate();
  const [heroSlide, setHeroSlide] = useState(0);
  const [hoverPanel, setHoverPanel] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setHeroSlide((s) => (s + 1) % 3), 3500);
    return () => clearInterval(t);
  }, []);

  const panelFlex = (id) => {
    if (hoverPanel === id) return 1.5;
    if (hoverPanel && hoverPanel !== id) return 0.7;
    return 1;
  };

  return (
    <div className="mtk-landing mtk-home">
      <section className="mtk-hero-intro">
        <div className="mtk-wrap">
          <h1>
            One platform to connect Shippers and Carriers and offers complete
            visibility on shipments.
          </h1>
          <p>
            Find the right transport partners and make every shipment easier to
            manage.
          </p>
          <Link to="/demo" className="mtk-btn-pill">
            Book a demo →
          </Link>
        </div>
      </section>

      <section className="mtk-audiences">
        <div className="mtk-audiences-grid">
          <div className="mtk-panels">
            <div
              className="mtk-panel"
              style={{ flex: panelFlex("shipper") }}
              onMouseEnter={() => setHoverPanel("shipper")}
              onMouseLeave={() => setHoverPanel(null)}
              onClick={() => navigate("/solutions")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/solutions")}
              role="button"
              tabIndex={0}
            >
              <div
                className="bg"
                style={{ backgroundImage: `url(${landingAsset("shipper-office.webp")})` }}
              />
              <div className="grad" />
              <span>Shipper</span>
            </div>
            <div
              className="mtk-panel"
              style={{ flex: panelFlex("carrier") }}
              onMouseEnter={() => setHoverPanel("carrier")}
              onMouseLeave={() => setHoverPanel(null)}
              onClick={() => navigate("/solutions/carrier")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/solutions/carrier")}
              role="button"
              tabIndex={0}
            >
              <div
                className="bg"
                style={{ backgroundImage: `url(${landingAsset("carrier-fleet.webp")})` }}
              />
              <div className="grad" />
              <span>Carrier</span>
            </div>
          </div>
          <div className="mtk-audiences-copy">
            <h2>Built for shippers and carriers who move fast.</h2>
            <p>
              One place to connect shippers and carriers, manage quotes, track
              every shipment and collect e-documents.
            </p>
          </div>
        </div>
      </section>

      <section className="mtk-hero-carousel">
        {SLIDES.map((slide, i) => (
          <div
            key={slide.title}
            className={`mtk-slide${heroSlide === i ? " active" : ""}`}
          >
            <div
              className="bg"
              style={{ backgroundImage: `url(${landingAsset(slide.img)})` }}
            />
            <div className="grad" />
            <div className="caption">
              <h3>{slide.title}</h3>
            </div>
          </div>
        ))}
        <div className="mtk-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              className={heroSlide === i ? "on" : ""}
              onClick={() => setHeroSlide(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      <section className="mtk-stats">
        <div className="mtk-wrap">
          <div className="mtk-stats-grid">
            <div className="mtk-stat">
              <div className="icon-box">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#FFCC57" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l7 3.2v5.3c0 4.6-3 7.9-7 9.5-4-1.6-7-4.9-7-9.5V6.2z" />
                  <path d="M9 12l2 2 4-4.5" />
                </svg>
              </div>
              <span className="label">Qualified carriers</span>
            </div>
            <div className="mtk-stat">
              <div className="icon-box">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#FFCC57" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3z" />
                </svg>
              </div>
              <span className="label">Operating across Europe</span>
            </div>
            <div className="mtk-stat">
              <div className="icon-box">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#FFCC57" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 17V8a1 1 0 0 1 1-1h9v10" />
                  <path d="M12 10h5.5l3.5 3.5V17h-3" />
                  <circle cx="7" cy="17.5" r="2" />
                  <circle cx="17" cy="17.5" r="2" />
                  <path d="M9 17.5h6" />
                </svg>
              </div>
              <span className="label">Optimised deliveries</span>
            </div>
            <div className="mtk-stat">
              <div className="icon-box">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#FFCC57" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2.6 6.3 6.8.5-5.2 4.4 1.7 6.6L12 16.3 6.1 19.8l1.7-6.6-5.2-4.4 6.8-.5z" />
                </svg>
              </div>
              <span className="label">High customer satisfaction</span>
            </div>
          </div>
        </div>
      </section>

      <section className="mtk-truck-types mtk-wrap">
        <span className="mtk-chip">Transport via 3 channels</span>
        <div className="mtk-truck-grid">
          {[
            { img: "cargo.webp", title: "General Cargo", sub: "FTL & LTL, palletised freight" },
            { img: "tanker.webp", title: "Tanker", sub: "Bulk liquid & chemical transport" },
            { img: "refrigerated.webp", title: "Refrigerated", sub: "Temperature-controlled" },
          ].map((t) => (
            <div key={t.title}>
              <div className="thumb">
                <img src={landingAsset(t.img)} alt={t.title} />
              </div>
              <h3>{t.title}</h3>
              <p>{t.sub}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mtk-dashboard-band">
        <div className="mtk-dashboard-grid">
          <div className="copy">
            <h2>
              Get full visibility into all
              <br />
              your orders in one dashboard.
            </h2>
            <p>
              Stay informed at every step of the delivery process, and give your
              team a better tool for operational efficiency.
            </p>
          </div>
          <div className="frame">
            <img
              className="orders-shot"
              src={landingAsset("shipper-account.png")}
              alt="Orders dashboard"
            />
          </div>
        </div>
      </section>

      <section className="mtk-quote-strip">
        <div>
          &quot;Every load stays visible, from loading dock to delivery.&quot;
        </div>
        <p>Shipment status and ETA, right in your dashboard.</p>
      </section>

      <section className="mtk-tracking-showcase">
        <span className="eyebrow">Tracking</span>
        <h2>Live tracking, on every route.</h2>
        <div className="frame-wrap">
          <img
            className="tracking-shot"
            src={landingAsset("tracking-real.png")}
            alt="Live tracking"
          />
        </div>
      </section>

      <section className="mtk-how mtk-wrap">
        <div className="head">
          <span className="eyebrow">How it works</span>
          <h2>From request to delivery, tracked end to end</h2>
        </div>
        <div className="steps">
          <div className="line" />
          {["Request a quote", "Compare and choose your carrier", "Track live, get your e-documents"].map(
            (t, i) => (
              <div key={t} className="step">
                <div className="num">{i + 1}</div>
                <div className="title">{t}</div>
              </div>
            )
          )}
        </div>
      </section>

      <section className="mtk-cta-band">
        <div className="mtk-cta-grid">
          <div className="copy">
            <span className="eyebrow">Get in touch</span>
            <h2>Create your account today.</h2>
            <p>
              Whether you&apos;re shipping goods or hauling them, your dedicated
              logistics partner is one signup away.
            </p>
          </div>
          <div className="actions">
            <Link to="/join" className="action gold">
              <span className="kicker">SHIPPERS &amp; CARRIERS</span>
              <span className="row">
                <span>Get started</span>
                <span>→</span>
              </span>
            </Link>
            <Link to="/contact" className="action navy">
              <span className="kicker">FOR EVERYONE ELSE</span>
              <span className="row">
                <span>Contact us</span>
                <span>→</span>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
